import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ExpandedListTab from '../ExpandedListTab';
import {
    requestRateLimitPropertiesType,
    requestRateLimitsType,
} from '../../types';
import ReadonlyNumberField from '../fields/ReadonlyNumberField';
import RequestRateLimits from './RequestRateLimits';
import ApplyChangesButton from '../buttons/ApplyChangesButton';
import useExtendedMutation from '../../hooks/useExtendedMutation';
import {
    ALL_REQUEST_RATE_LIMITS_QUERY_NAME,
    UPDATE_REQUEST_RATE_LIMITS,
} from '../../graphql/settings';
import useExtendedState from '../../hooks/useExtendedState';

RequestRatesAndLimitsDetailTab.propTypes = {
    requestRateLimits: requestRateLimitsType.isRequired,
    requestRateLimitProps: requestRateLimitPropertiesType.isRequired,
};

function RequestRatesAndLimitsDetailTab(props) {
    const {
        requestRateLimits,
        requestRateLimitProps,
    } = props;
    const { t } = useTranslation();
    const currentRequestRateLimitsState = useExtendedState(requestRateLimits.map((requestRateLimit) => ({
        limitId: requestRateLimit.limitId,
        user: requestRateLimit.user,
        requestType: requestRateLimit.requestType,
        value: requestRateLimit.value,
        messageRate: requestRateLimit.messageRate,
        isValid: true,
    })));
    const [doReset, setDoReset] = useState(false);
    const [backendChanged, setBackendChanged] = useState(false);
    const onRequestRateLimitCreate = (limitId, user, requestType, value) => {
        currentRequestRateLimitsState.setValue([...currentRequestRateLimitsState.value, {
            limitId,
            user,
            requestType,
            value,
            messageRate: null,
        }]);
        setBackendChanged(false);
        setDoReset(false);
    };
    const onRequestRateLimitDelete = (user, requestType) => {
        const newRequestRateLimits = currentRequestRateLimitsState.value.filter((currentRequestRateLimit) => !(currentRequestRateLimit.user === user && currentRequestRateLimit.requestType === requestType));
        currentRequestRateLimitsState.setValue(newRequestRateLimits);
        setBackendChanged(false);
        setDoReset(false);
    };
    const onRequestRateLimitChange = (requestRateLimitToChange) => (newValue, isValid) => {
        const newRequestRateLimits = currentRequestRateLimitsState.value.map((requestRateLimit) => {
            let result = requestRateLimit;
            if (requestRateLimit === requestRateLimitToChange) {
                result = { ...requestRateLimit, value: newValue, isValid };
            }
            return result;
        });
        currentRequestRateLimitsState.setValue(newRequestRateLimits);
        setBackendChanged(false);
        setDoReset(false);
    };
    const onReset = () => {
        currentRequestRateLimitsState.reset();
        setDoReset(true);
    };
    const isInvalidRequestRateLimit = () => currentRequestRateLimitsState.value.some((currentRequestRateLimit) => currentRequestRateLimit.isValid === false);
    const isApplyChangesDisabled = !currentRequestRateLimitsState.isChanged || isInvalidRequestRateLimit();
    const updateRequestRateLimitsExtendedMutation = useExtendedMutation(UPDATE_REQUEST_RATE_LIMITS, {
        variables: {
            requestRateLimits: currentRequestRateLimitsState.value.filter((rrl) => rrl.value).map((newRequestRateLimit) => ({
                userRacf: newRequestRateLimit.user === null ? null : newRequestRateLimit.user.racf,
                requestType: newRequestRateLimit.requestType,
                value: newRequestRateLimit.value,
            })),
        },
        successCallback: () => {
            currentRequestRateLimitsState.changeInitialState(currentRequestRateLimitsState.value);
            currentRequestRateLimitsState.reset();
            setBackendChanged(true);
            setDoReset(false);
        },
        successMessageOptions: { key: 'settings.request-rates-and-limits.notification.request-rate-limits-update-success', dataProperty: 'updateRequestRateLimits' },
        failureMessageOptions: { key: 'settings.request-rates-and-limits.notification.request-rate-limits-update-failure' },
        refetchQueries: [ALL_REQUEST_RATE_LIMITS_QUERY_NAME],
    });
    return (
        <ExpandedListTab
            className="settings-detail-tab"
        >
            <div className="rich-list-row-expanded--fields">
                <ReadonlyNumberField
                    className="request-rates-and-limits-detail-tab--fields--period"
                    label={t('settings.request-rates-and-limits.period.label')}
                    value={requestRateLimitProps.period}
                    postfix={` ${t('settings.request-rates-and-limits.period.unit')}`}
                />
                <ReadonlyNumberField
                    className="request-rates-and-limits-detail-tab--fields--default-rate-limit"
                    label={t('settings.request-rates-and-limits.default-rate-limit.label')}
                    value={requestRateLimitProps.defaultRateLimit}
                    postfix={` ${t('settings.request-rates-and-limits.default-rate-limit.unit')}`}
                />
                <RequestRateLimits
                    requestRateLimits={currentRequestRateLimitsState.value}
                    requestRateLimitProps={requestRateLimitProps}
                    onRequestRateLimitCreate={onRequestRateLimitCreate}
                    onRequestRateLimitDelete={onRequestRateLimitDelete}
                    onRequestRateLimitChange={onRequestRateLimitChange}
                    onReset={onReset}
                    isChanged={currentRequestRateLimitsState.isChanged}
                    backendChanged={backendChanged}
                    doReset={doReset}
                />
            </div>
            <div className="rich-list-row-expanded--buttons">
                <div className="rich-list-row-expanded--buttons--left">
                    <ApplyChangesButton
                        confirmationMessage={t('settings.request-rates-and-limits.confirm-update-request-rate-limits')}
                        extendedMutation={updateRequestRateLimitsExtendedMutation}
                        disabled={isApplyChangesDisabled}
                        showConfirmationDialog
                    />
                </div>
            </div>
        </ExpandedListTab>
    );
}

export default RequestRatesAndLimitsDetailTab;
